import { useEffect, useState } from 'react';
import { getLoyversePresence } from '../api/loyverse';
import { useAuth } from '../auth/AuthProvider';

/**
 * Pre-client activation hardening (ZTV-04): ¿este negocio tiene la integración Loyverse conectada?
 *
 * - `true`  → canal Loyverse activo: la UI conserva TODO el comportamiento existente.
 * - `false` → el negocio no usa Loyverse: la UI lo presenta como integración opcional disponible
 *   (sin CTAs primarias de sincronizar, sin KPIs vacíos dominando pantallas).
 * - `null`  → aún no se sabe (cargando o la consulta falló). Ante la duda la UI conserva el
 *   comportamiento existente: un negocio CON Loyverse jamás pierde funciones por un parpadeo.
 *
 * Loyverse es una fuente/integración, no la definición de una venta: este flag es solo presentación.
 * La respuesta se cachea por negocio para toda la sesión de la pestaña (una sola llamada);
 * conectar/desconectar la integración se refleja al recargar, igual que el resto de la página de
 * Conexión Loyverse.
 */
let cache: { businessId: string; connected: boolean } | null = null;
let inflight: { businessId: string; promise: Promise<boolean | null> } | null = null;

async function fetchPresence(businessId: string): Promise<boolean | null> {
  try {
    const { connected } = await getLoyversePresence();
    cache = { businessId, connected };
    return connected;
  } catch {
    return null; // sin respuesta no se afirma nada: la UI conserva el comportamiento existente
  } finally {
    if (inflight?.businessId === businessId) inflight = null;
  }
}

export function useLoyversePresence(): boolean | null {
  const { business } = useAuth();
  const businessId = business?.id ?? null;
  const [connected, setConnected] = useState<boolean | null>(() =>
    businessId && cache?.businessId === businessId ? cache.connected : null,
  );

  useEffect(() => {
    if (!businessId) return;
    if (cache?.businessId === businessId) { setConnected(cache.connected); return; }
    if (!inflight || inflight.businessId !== businessId) {
      inflight = { businessId, promise: fetchPresence(businessId) };
    }
    let alive = true;
    void inflight.promise.then((value) => { if (alive) setConnected(value); });
    return () => { alive = false; };
  }, [businessId]);

  return connected;
}

/** Solo para pruebas: restablece el caché del módulo. */
export function __resetLoyversePresenceCache(): void {
  cache = null;
  inflight = null;
}
